import { ExpandLess, ExpandMore } from "@mui/icons-material";
import { Box, ButtonBase, Collapse, Typography } from "@mui/material";
import React, { useState } from "react";

import { monospaceStyle } from "../../theme";
import { formatFilterCount } from "./FilterRow";

export interface FilterSectionProps {
  // The section's name, shown in upper case
  label: string;
  // Whether the section starts open
  defaultOpen?: boolean;
  // A number shown in monospace at the right of the section's head, e.g.
  // the keys or values the section holds
  count?: number;
  // A quiet line under the section's content
  note?: string;
  children: React.ReactNode;
}

// One section of a filter panel: a small label that folds and unfolds what
// is beneath it. Whether it is open is the section's own matter and is kept
// nowhere else. Sections are set apart by the space above their label, with
// no rule between them.
const FilterSection: React.FC<FilterSectionProps> = ({
  label,
  defaultOpen = true,
  count,
  note,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <Box component="section" aria-label={label} sx={{ px: 1.5, pt: 1.5 }}>
      <ButtonBase
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
          color: "text.secondary",
        }}
      >
        {/* On one line and cut off at the end when the panel is narrow,
            like the values beneath it */}
        <Typography
          component="span"
          variant="caption"
          noWrap
          sx={{
            minWidth: 0,
            textAlign: "left",
            fontSize: "0.72rem",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            fontWeight: 500,
          }}
        >
          {label}
        </Typography>
        <Box
          component="span"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {count !== undefined && (
            <Typography
              component="span"
              variant="caption"
              sx={monospaceStyle}
            >
              {formatFilterCount(count)}
            </Typography>
          )}
          {open ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          )}
        </Box>
      </ButtonBase>
      <Collapse in={open} timeout={120} unmountOnExit>
        <Box sx={{ pt: 0.5 }}>
          {children}
          {note && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", pt: 0.5, lineHeight: 1.35 }}
            >
              {note}
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

export default FilterSection;
